import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Send, Image as ImageIcon, Trash2, MoreVertical, Search } from "lucide-react";
import { toast } from "sonner";
import AppShell from "@/components/AppShell";
import { useAuth, ROLE_THEMES } from "@/context/AuthContext";
import { useNotifications } from "@/context/NotificationsContext";
import { useI18n } from "@/context/I18nContext";
import { api } from "@/lib/api";

const MAX_IMAGE_BYTES = 2 * 1024 * 1024; // 2 MB

function TypingBubble() {
  return (
    <div className="flex justify-start" data-testid="typing-bubble">
      <div className="max-w-[70%] px-4 py-3 rounded-2xl bg-white border border-gray-200 inline-flex items-center gap-1">
        <span className="h-2 w-2 rounded-full bg-gray-400 animate-bounce" style={{ animationDelay: "0ms" }} />
        <span className="h-2 w-2 rounded-full bg-gray-400 animate-bounce" style={{ animationDelay: "150ms" }} />
        <span className="h-2 w-2 rounded-full bg-gray-400 animate-bounce" style={{ animationDelay: "300ms" }} />
      </div>
    </div>
  );
}

export default function Messages() {
  const { threadId } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const { tick, sendTyping, typingFrom } = useNotifications();
  const { t } = useI18n();
  const theme = user ? ROLE_THEMES[user.role] : ROLE_THEMES.farmer;
  const [threads, setThreads] = useState([]);
  const [msgs, setMsgs] = useState([]);
  const [body, setBody] = useState("");
  const [openMenuId, setOpenMenuId] = useState(null);
  const [threadQuery, setThreadQuery] = useState("");
  const [threadFilter, setThreadFilter] = useState("all"); // all/farmer/day_worker/investor/recent
  const scrollRef = useRef();
  const fileInputRef = useRef();
  const typingTimeoutRef = useRef();

  const activeThread = threads.find((t) => t.thread_id === threadId);
  const otherId = activeThread?.other_user_id;
  const isPeerTyping = typingFrom && otherId && typingFrom[otherId] === threadId;

  useEffect(() => {
    api.get("/messages/threads").then((r) => setThreads(r.data)).catch(() => {});
  }, [threadId, tick]);

  const filteredThreads = useMemo(() => {
    const q = threadQuery.trim().toLowerCase();
    let list = threads;
    if (q) list = list.filter((t) => (t.other_user_name || "").toLowerCase().includes(q) || (t.last_message || "").toLowerCase().includes(q));
    if (threadFilter === "recent") {
      const dayAgo = Date.now() - 24 * 3600 * 1000;
      list = list.filter((t) => new Date(t.last_at).getTime() >= dayAgo);
    } else if (["farmer", "day_worker", "investor"].includes(threadFilter)) {
      list = list.filter((t) => t.other_user_role === threadFilter);
    }
    return list;
  }, [threads, threadQuery, threadFilter]);

  const deleteThread = async (tid, e) => {
    e?.stopPropagation();
    if (!window.confirm("Remove this conversation from your inbox? The other person will still see it on their side.")) return;
    try {
      await api.delete(`/messages/thread/${tid}`);
      setThreads((prev) => prev.filter((t) => t.thread_id !== tid));
      if (tid === threadId) nav("/messages");
      toast.success("Conversation removed");
    } catch (err) {
      toast.error("Could not remove conversation");
    }
  };

  useEffect(() => {
    if (!threadId) return;
    api.get(`/messages/${threadId}`).then((r) => setMsgs(r.data)).catch(() => {});
    api.post(`/messages/read/${threadId}`).catch(() => {});
  }, [threadId, tick]);

  useEffect(() => { scrollRef.current?.scrollTo({ top: 9999, behavior: "smooth" }); }, [msgs, isPeerTyping]);

  const emitTyping = (isTyping) => {
    if (!otherId) return;
    sendTyping?.({ to_user_id: otherId, thread_id: threadId, is_typing: isTyping });
  };

  const handleInput = (e) => {
    setBody(e.target.value);
    emitTyping(true);
    clearTimeout(typingTimeoutRef.current);
    typingTimeoutRef.current = setTimeout(() => emitTyping(false), 1800);
  };

  const send = async (extra = {}) => {
    if (!activeThread) return;
    if (!body.trim() && !extra.image_url) return;
    emitTyping(false);
    clearTimeout(typingTimeoutRef.current);
    await api.post("/messages", { to_user_id: otherId, body: body.trim(), image_url: extra.image_url });
    setBody("");
    const r = await api.get(`/messages/${threadId}`);
    setMsgs(r.data);
  };

  const onImagePick = (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (f.size > MAX_IMAGE_BYTES) return toast.error("Image is too large. Max 2 MB.");
    const reader = new FileReader();
    reader.onload = () => send({ image_url: reader.result });
    reader.readAsDataURL(f);
  };

  const deleteMsg = async (id) => {
    setOpenMenuId(null);
    try {
      await api.delete(`/messages/${id}`);
      setMsgs((prev) => prev.map((m) => (m.id === id ? { ...m, deleted: true, body: "", image_url: null } : m)));
      toast.success("Message deleted");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Delete failed");
    }
  };

  return (
    <AppShell>
      <div className="px-4 md:px-8 lg:px-12 py-6 h-[calc(100vh-72px)] flex gap-6">
        <div className="w-full max-w-xs rounded-3xl bg-white border border-gray-100 overflow-hidden flex flex-col">
          <div className="p-4 border-b border-gray-100">
            <div className="text-xs uppercase tracking-widest text-gray-500">{t("nav.messages")}</div>
            <div className="text-lg font-bold text-gray-900">{t("msg.threads")}</div>
            <div className="mt-3 relative">
              <Search className="h-4 w-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <input data-testid="thread-search" value={threadQuery} onChange={(e) => setThreadQuery(e.target.value)}
                placeholder="Search contacts or messages"
                className="w-full pl-9 pr-3 py-2 rounded-full bg-gray-50 border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500" />
            </div>
            <div className="mt-2 flex gap-1 overflow-x-auto pb-1">
              {[
                { k: "all", l: "All" },
                { k: "recent", l: "Recent" },
                { k: "farmer", l: "Farmers" },
                { k: "day_worker", l: "Day Workers" },
                { k: "investor", l: "Investors" },
              ].map((f) => (
                <button key={f.k} data-testid={`thread-filter-${f.k}`}
                  onClick={() => setThreadFilter(f.k)}
                  className={`shrink-0 px-3 py-1 rounded-full text-[11px] font-medium ${threadFilter === f.k ? `${theme.bg} text-white` : "bg-gray-50 border border-gray-200 text-gray-600"}`}>{f.l}</button>
              ))}
            </div>
          </div>
          <div className="overflow-y-auto flex-1">
            {filteredThreads.length === 0 && <div className="p-6 text-sm text-gray-500">{threadQuery || threadFilter !== "all" ? "No matches" : t("msg.no_convos")}</div>}
            {filteredThreads.map((th) => (
              <div key={th.thread_id} className={`group relative w-full flex items-center gap-3 hover:bg-gray-50 border-b border-gray-50 ${threadId === th.thread_id ? theme.bgSoft : ""}`}>
                <button data-testid={`thread-${th.thread_id}`} onClick={() => nav(`/messages/${th.thread_id}`)}
                  className="flex-1 text-left p-4 flex items-center gap-3 min-w-0">
                  <div className={`h-10 w-10 rounded-full ${theme.bg} text-white flex items-center justify-center font-bold`}>{th.other_user_name?.[0]}</div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5">
                      <div className="font-semibold text-gray-900 text-sm truncate">{th.other_user_name}</div>
                      {th.other_user_role && <span className="text-[9px] uppercase tracking-widest text-gray-400 shrink-0">{th.other_user_role.replace("_", " ")}</span>}
                    </div>
                    <div className="text-xs text-gray-500 truncate">{th.last_message || "(image)"}</div>
                  </div>
                </button>
                <button data-testid={`thread-delete-${th.thread_id}`} onClick={(e) => deleteThread(th.thread_id, e)}
                  className="opacity-0 group-hover:opacity-100 md:opacity-100 mr-3 h-8 w-8 rounded-full bg-white border border-gray-200 text-rose-500 hover:bg-rose-50 flex items-center justify-center transition">
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
            ))}
          </div>
        </div>

        <div className="flex-1 rounded-3xl bg-white border border-gray-100 flex flex-col overflow-hidden">
          {!threadId ? (
            <div className="flex-1 flex items-center justify-center text-gray-500">{t("msg.select_conv")}</div>
          ) : (
            <>
              <div className="p-4 border-b border-gray-100 flex items-center gap-2">
                <div className="font-bold text-gray-900">{activeThread?.other_user_name || t("nav.messages")}</div>
                {isPeerTyping && <span className="text-xs text-emerald-600 animate-pulse">typing…</span>}
              </div>
              <div ref={scrollRef} className="flex-1 overflow-y-auto p-6 space-y-3 bg-gray-50">
                {msgs.map((m) => {
                  const mine = m.from_user_id === user.id;
                  const isDeleted = m.deleted;
                  return (
                    <div key={m.id} className={`group flex items-end gap-2 ${mine ? "justify-end" : "justify-start"}`} data-testid={`msg-${m.id}`}>
                      {mine && !isDeleted && (
                        <div className="relative">
                          <button data-testid={`msg-menu-${m.id}`} onClick={() => setOpenMenuId(openMenuId === m.id ? null : m.id)}
                            className="opacity-0 group-hover:opacity-100 md:opacity-100 h-7 w-7 rounded-full bg-white border border-gray-200 text-gray-500 flex items-center justify-center transition">
                            <MoreVertical className="h-3.5 w-3.5" />
                          </button>
                          {openMenuId === m.id && (
                            <div className="absolute right-0 bottom-8 z-20 bg-white rounded-xl shadow-lg border border-gray-100 py-1 w-32">
                              <button data-testid={`msg-delete-${m.id}`} onClick={() => deleteMsg(m.id)}
                                className="w-full text-left px-3 py-2 text-xs text-rose-600 hover:bg-rose-50 flex items-center gap-2">
                                <Trash2 className="h-3 w-3" />Delete
                              </button>
                            </div>
                          )}
                        </div>
                      )}
                      <div className={`max-w-[70%] px-4 py-2.5 rounded-2xl break-words ${isDeleted ? "bg-gray-100 text-gray-400 italic border border-dashed border-gray-300" : mine ? `${theme.bg} text-white` : "bg-white border border-gray-200 text-gray-900"}`}>
                        {isDeleted ? (
                          <span className="text-xs">🚫 message deleted</span>
                        ) : (
                          <>
                            {m.image_url && (
                              <button type="button" onClick={() => window.open(m.image_url, "_blank")}
                                className="block mb-1 rounded-xl overflow-hidden bg-black/10 max-w-full">
                                <img src={m.image_url} alt="attachment" className="max-h-64 w-auto object-contain" />
                              </button>
                            )}
                            {m.body && renderBodyWithLinks(m.body, mine)}
                            {mine && <span data-testid={`read-${m.id}`} className={`ml-1 text-[10px] align-middle ${m.read ? "text-white" : "text-white/60"}`}>{m.read ? "✓✓" : "✓"}</span>}
                          </>
                        )}
                      </div>
                    </div>
                  );
                })}
                {isPeerTyping && <TypingBubble />}
              </div>
              <div className="p-4 border-t border-gray-100 flex gap-2 items-center">
                <button type="button" onClick={() => setBody((b) => b + "😊")} className="h-12 w-12 rounded-full bg-white border border-gray-200 text-lg" title="Emoji">😊</button>
                <button data-testid="msg-image-btn" type="button" onClick={() => fileInputRef.current?.click()}
                  className="h-12 w-12 rounded-full bg-white border border-gray-200 text-gray-700 flex items-center justify-center hover:bg-gray-50" title="Send image">
                  <ImageIcon className="h-5 w-5" />
                </button>
                <input ref={fileInputRef} type="file" accept="image/*" hidden onChange={onImagePick} data-testid="msg-image-input" />
                <input data-testid="message-input" value={body} onChange={handleInput} onKeyDown={(e) => e.key === "Enter" && send()}
                  placeholder={t("msg.type")} className="flex-1 rounded-full border border-gray-200 px-5 py-3" />
                <button data-testid="message-send" onClick={() => send()} className={`h-12 w-12 rounded-full ${theme.bg} text-white flex items-center justify-center`}><Send className="h-4 w-4" /></button>
              </div>
            </>
          )}
        </div>
      </div>
    </AppShell>
  );
}

function renderBodyWithLinks(body, mine) {
  const parts = body.split(/(https?:\/\/[^\s]+)/g);
  return parts.map((p, i) => /^https?:\/\//.test(p)
    ? <a key={`${i}-${p.slice(0, 12)}`} href={p} target="_blank" rel="noreferrer" className={`underline ${mine ? "text-white" : "text-emerald-700"}`}>{p}</a>
    : <span key={`${i}-t-${p.slice(0, 8)}`}>{p}</span>);
}
